/**
 * Writing activity on a profile's left column: the summary only (the writing score of the last 12 weeks,
 * its trend, and the last 26 weeks as a strip coloured by each week's score); everything the score is
 * made of is on the Stats tab. The owner always sees it and decides with the eye button whether visitors
 * see it too (the "writing_summary" stats card); visitors see it only then.
 * Data: own GET /users/me/stats/, a visitor's GET /users/<id>/stats/ (registry/user/my_stats.py).
 */
import React from 'react';
import useSWR from 'swr';
import { Box, Button, Paper, Tooltip, Typography } from '@mui/material';
import { EditNote as WritingIcon } from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { ShareToggle } from './MyStatsTab';
import { fill, fmtWeek, streak, wrote } from './WritingHeatmap';

const STRIP = 26;
const KEY = 'writing_summary';

export default function WritingSummaryCard({ userId, isOwn, onOpenStats }) {
  const url = isOwn ? '/users/me/stats/' : (userId ? `/users/${userId}/stats/` : null);
  const { data, mutate } = useSWR(url, fetcher, { revalidateOnFocus: false });
  if (!data) return null;
  const shown = new Set(data.public || []);
  // own: the full stats; a visitor: only what was shared
  const summary = isOwn
    ? (data.writing_score && { score: data.writing_score.score, previous: data.writing_score.previous,
      weeks: (data.writing_weeks || []).slice(-STRIP) })
    : data.writing_summary;
  if (!summary || (!isOwn && !shown.has(KEY))) return null;

  const weeks = summary.weeks || [];
  const run = streak(weeks);
  const active = weeks.some(wrote);
  const trend = summary.previous == null ? null : summary.score - summary.previous;
  const toggle = isOwn ? async () => {
    const next = shown.has(KEY) ? [...shown].filter((k) => k !== KEY) : [...shown, KEY];
    mutate({ ...data, public: next }, false);
    try { await axiosService.patch('/users/me/stats/', { public: next }); } finally { mutate(); }
  } : null;

  return (
    <Paper sx={{ mt: 2, borderRadius: 2, p: 2 }}>
      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <WritingIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ flex: 1 }}>Writing activity</Typography>
        {run > 0 && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{run === 1 ? 'writing this week' : `${run} weeks in a row`}</Typography>}
        <ShareToggle shared={shown.has(KEY)} onToggle={toggle} />
      </Box>

      {active ? (
        <>
          <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
            <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>{summary.score}</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>writing score</Typography>
            {trend != null && trend !== 0 && (
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: trend > 0 ? 'success.main' : 'text.secondary' }}>
                {trend > 0 ? '▲' : '▼'} {Math.abs(trend)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${weeks.length}, 1fr)`, gap: '2px', mt: 1.5, mb: 0.5 }}
            role="img" aria-label={`Writing score in the last ${weeks.length} weeks`}>
            {weeks.map((w) => (
              <Tooltip key={w.week_start} title={`Week of ${fmtWeek(w.week_start)}: ${w.score ? `score ${w.score}` : 'no writing'}`}>
                <Box sx={{ height: 16, borderRadius: '2px', bgcolor: fill(w.score) || 'action.hover' }} />
              </Tooltip>
            ))}
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{weeks[0] && fmtWeek(weeks[0].week_start)}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>this week</Typography>
          </Box>
        </>
      ) : (
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
          {isOwn ? 'Writing in a proposal, a publication, a reflection or a post builds your writing score here.' : 'No writing in the last months.'}
        </Typography>
      )}
      {isOwn && onOpenStats && <Button size="small" onClick={onOpenStats} sx={{ mt: 1, px: 0 }}>Details in Stats</Button>}
    </Paper>
  );
}
